import { useEffect, useRef, useState, memo, useCallback } from 'react';

function About() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);

  const scrollToContact = useCallback(() => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    setTimeout(() => {
      const nameInput = document.getElementById('name') as HTMLInputElement;
      nameInput?.focus();
    }, 500);
  }, []);

  return (
    <section id="about" ref={sectionRef} className="py-16 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Text block */}
          <div
            className={`flex flex-col transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            {/* Header and paragraphs - order 1 on mobile */}
            <div className="order-1">
              <p className="text-sky-600 text-xl font-semibold mb-2">
                Davis County's Trusted Choice
              </p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
                Local Experts Who Care About Your Home
              </h2>
              <p className="text-gray-600 text-lg sm:text-xl leading-relaxed mb-4">
                Our founder Rob Smith was born and raised right here in Northern Utah, and this community isn't just where he works — it's where he coaches little league, shops for groceries, and waves to his neighbors. That personal connection is something no national franchise can replicate, and it shapes every decision we make.
              </p>
              <p className="text-gray-600 text-lg sm:text-xl leading-relaxed mb-4">
                We take the time to actually listen. Your situation is unique, your home is unique, and the plan we build for you will be too. Throughout the entire process, we stay in constant contact so you're never left wondering what's going on or what comes next.
              </p>
              <p className="text-gray-600 text-lg sm:text-xl leading-relaxed mb-6">
                With 19 years of restoration and disaster cleanup experience under his belt and more than 6,000 completed projects, Rob brings a level of skill and accountability that speaks for itself. You won't get a call center — you'll get a real person who knows this trade inside and out and who loses sleep over doing the job right.
              </p>
            </div>

            {/* Image block - order 2 on mobile, hidden on lg (shown in separate column) */}
            <div
              className={`order-2 lg:hidden relative mb-6 transition-all duration-1000 delay-200 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
              }`}
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/edited_2542e5269ac939e153790b793ed4888f_12fa7421.jpg"
                  alt="Davis County Disaster Cleanup Team"
                  className="w-full h-[300px] sm:h-[400px] object-cover object-top"
                  loading="lazy"
                  decoding="async"
                />
              </div>

              {/* Emergency badge */}
              <div className="absolute -bottom-4 sm:-bottom-6 left-2 sm:-left-6 bg-sky-600 text-white p-4 sm:p-6 rounded-xl shadow-xl">
                <p className="text-3xl sm:text-4xl font-bold mb-1">24/7</p>
                <p className="text-xs sm:text-sm">Emergency Response</p>
              </div>

              {/* Certification badge */}
              <div className="absolute -top-3 sm:-top-4 right-2 sm:-right-4 bg-white p-3 sm:p-4 rounded-xl shadow-xl">
                <div className="flex items-center gap-2">
                  <i className="ri-verified-badge-fill text-xl sm:text-2xl text-sky-600"></i>
                  <div>
                    <p className="text-[10px] sm:text-xs text-gray-500">Certified by</p>
                    <p className="text-xs sm:text-sm font-bold text-gray-900">IICRC</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats - order 3 on mobile */}
            <div className="order-3 grid grid-cols-3 gap-3 sm:gap-4 mb-8">
              <div className="text-center p-3 sm:p-4 bg-sky-50 rounded-xl">
                <p className="text-2xl sm:text-3xl font-bold text-sky-600 mb-1">19+</p>
                <p className="text-gray-600 text-[10px] sm:text-xs">Years Experience</p>
              </div>
              <div className="text-center p-3 sm:p-4 bg-sky-50 rounded-xl">
                <p className="text-2xl sm:text-3xl font-bold text-sky-600 mb-1">6,000+</p>
                <p className="text-gray-600 text-[10px] sm:text-xs">Projects Managed</p>
              </div>
              <div className="text-center p-3 sm:p-4 bg-sky-50 rounded-xl">
                <p className="text-2xl sm:text-3xl font-bold text-sky-600 mb-1">4.7★</p>
                <p className="text-gray-600 text-[10px] sm:text-xs">Google Rating</p>
              </div>
            </div>

            {/* CTA - order 4 on mobile */}
            <div className="order-4 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center sm:justify-start items-center">
              <button
                onClick={scrollToContact}
                className="w-full sm:w-auto bg-sky-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold transition-all duration-200 hover:brightness-90 hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Get Your Free Assessment
              </button>
              <a
                href="tel:3853406632"
                className="w-full sm:w-auto text-center border border-gray-300 text-gray-900 px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold transition-all duration-200 hover:brightness-90 hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                Call Now: (385) 340-6632
              </a>
            </div>
          </div>

          {/* Image block - desktop only (right column) */}
          <div
            className={`hidden lg:block relative transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/edited_2542e5269ac939e153790b793ed4888f_12fa7421.jpg"
                alt="Davis County Disaster Cleanup Team"
                className="w-full h-[500px] object-cover object-top"
                loading="lazy"
                decoding="async"
              />
            </div>

            {/* Emergency badge */}
            <div className="absolute -bottom-6 -left-6 bg-sky-600 text-white p-6 rounded-xl shadow-xl">
              <p className="text-4xl font-bold mb-1">24/7</p>
              <p className="text-sm">Emergency Response</p>
            </div>

            {/* Certification badge */}
            <div className="absolute -top-4 -right-4 bg-white p-4 rounded-xl shadow-xl">
              <div className="flex items-center gap-2">
                <i className="ri-verified-badge-fill text-2xl text-sky-600"></i>
                <div>
                  <p className="text-xs text-gray-500">Certified by</p>
                  <p className="text-sm font-bold text-gray-900">IICRC</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default memo(About);
